import { useState } from 'react';
import { useParams } from 'react-router';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  KeyValueList,
  KeyValueSkeleton,
  Link,
  MONO,
  OverflowMenu,
  Page,
  PageHeader,
  Select,
  Stack,
  TABULAR,
  useToast,
} from '@platform/design-system';
import type { platform } from '@platform/contracts';
import { RouterLink, describeError, useCanOpen } from '@platform/app-kit';
import {
  useAdoptPackage,
  useMeteringUsage,
  usePackages,
  useReapplyPackage,
  useResumeTenant,
  useSubscription,
  useSuspendTenant,
  useTenant,
} from '../api/queries.ts';
import {
  MODEL_LABEL,
  TIER_LABEL,
  assistantTotals,
  dollars,
  featureList,
  formatDate,
  formatNumber,
  utcPeriods,
} from '../shell/format.ts';
import { SpendBar } from '../shell/spend-bar.tsx';
import { AssistantStateBadge } from './usage-page.tsx';
import { BillingStatusBadge, TenantStatusBadge } from '../shell/labels.tsx';
import { ADOPT_ROUTE, REAPPLY_ROUTE, RESUME_ROUTE, SUSPEND_ROUTE } from '../shell/routes.ts';

/**
 * `/tenants/:id` — the registry record, the entitlement snapshot and the subscription. The
 * snapshot is the tenant's copy at the version applied; the catalog moving on never changes
 * it. "snapshot vN · package now vM" + Apply (`Tenants_reapplyPackage`) is the only way it
 * moves; adopting another package is `Tenants_adoptPackage`.
 */
export function TenantDetailPage() {
  const { id = '' } = useParams();
  const tenant = useTenant(id);
  const packages = usePackages(true);
  const subscription = useSubscription(id);
  const reapply = useReapplyPackage();
  const adopt = useAdoptPackage();
  const suspend = useSuspendTenant();
  const resume = useResumeTenant();
  const toast = useToast();
  const canReapply = useCanOpen(REAPPLY_ROUTE);
  const canAdopt = useCanOpen(ADOPT_ROUTE);
  const canSuspend = useCanOpen(SUSPEND_ROUTE);
  const canResume = useCanOpen(RESUME_ROUTE);
  const [confirmApply, setConfirmApply] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [adoptId, setAdoptId] = useState('');

  const t = tenant.data;
  const current = t?.entitlement
    ? packages.data?.find((p) => p.packageId === t.entitlement?.packageId)
    : undefined;
  const behind = Boolean(
    t?.entitlement && current && current.version > t.entitlement.packageVersion,
  );
  const adoptable = (packages.data ?? []).filter(
    (p) => t && !p.retired && p.tiers.includes(t.tier) && p.packageId !== t.entitlement?.packageId,
  );

  if (tenant.isError) {
    return (
      <Page>
        <PageHeader
          title="Tenant not found"
          breadcrumb={
            <Link component={RouterLink} href="/tenants">
              Tenants
            </Link>
          }
        />
        <EmptyState
          title="There is no tenant with this id"
          description={describeError(tenant.error)}
          action={
            <Button component={RouterLink} href="/tenants" variant="contained">
              Back to tenants
            </Button>
          }
        />
      </Page>
    );
  }

  const doApply = () => {
    if (!t || !current) return;
    reapply.mutate(t.tenantId, {
      onSuccess: () => {
        setConfirmApply(false);
        toast.success(`${current.name} v${current.version} applied to ${t.name}`);
      },
      onError: (e) => toast.error(describeError(e, 'Could not apply the package')),
    });
  };

  const doAdopt = () => {
    if (!t || !adoptId) return;
    const pkg = packages.data?.find((p) => p.packageId === adoptId);
    adopt.mutate(
      { tenantId: t.tenantId, packageId: adoptId },
      {
        onSuccess: () => {
          setAdoptId('');
          toast.success(`${t.name} moved to ${pkg?.name ?? adoptId}`);
        },
        onError: (e) => toast.error(describeError(e, 'Could not change the package')),
      },
    );
  };

  const doSuspend = () => {
    if (!t) return;
    suspend.mutate(t.tenantId, {
      onSuccess: () => {
        setConfirmSuspend(false);
        toast.success(`${t.name} suspended`);
      },
      onError: (e) => toast.error(describeError(e, 'Could not suspend the tenant')),
    });
  };

  const doResume = () => {
    if (!t) return;
    resume.mutate(t.tenantId, {
      onSuccess: () => toast.success(`${t.name} reinstated`),
      onError: (e) => toast.error(describeError(e, 'Could not reinstate the tenant')),
    });
  };

  const actions = t
    ? [
        ...(canSuspend && t.status !== 'suspended'
          ? [
              {
                id: 'suspend',
                label: 'Suspend tenant',
                destructive: true,
                onSelect: () => setConfirmSuspend(true),
              },
            ]
          : []),
        ...(canResume && t.status === 'suspended'
          ? [{ id: 'resume', label: 'Reinstate tenant', onSelect: doResume }]
          : []),
      ]
    : [];

  return (
    <Page>
      <PageHeader
        breadcrumb={
          <Link component={RouterLink} href="/tenants">
            Tenants
          </Link>
        }
        title={t?.name ?? 'Tenant'}
        meta={
          t ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Box component="span" sx={MONO}>
                {t.slug}
              </Box>
              <span>·</span>
              <TenantStatusBadge status={t.status} />
              {subscription.data && <BillingStatusBadge status={subscription.data.status} />}
            </Stack>
          ) : undefined
        }
        actions={
          t && actions.length > 0 ? (
            <OverflowMenu label={`Actions for ${t.name}`} size="medium" items={actions} />
          ) : undefined
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        <Card title="Tenant">
          {!t ? (
            <KeyValueSkeleton rows={7} />
          ) : (
            <KeyValueList
              items={[
                {
                  key: 'Id',
                  value: (
                    <Box component="span" sx={MONO}>
                      {t.tenantId}
                    </Box>
                  ),
                },
                { key: 'Tier', value: TIER_LABEL[t.tier] },
                {
                  key: 'Cell',
                  value: (
                    <Box component="span" sx={MONO}>
                      {t.cellId}
                    </Box>
                  ),
                },
                { key: 'Status', value: <TenantStatusBadge status={t.status} /> },
                { key: 'First admin', value: t.adminEmail },
                {
                  key: 'Flags',
                  value:
                    Object.entries(t.flags)
                      .filter(([, on]) => on)
                      .map(([k]) => k)
                      .join(', ') || 'None',
                },
                { key: 'Created', value: formatDate(t.createdAt) },
              ]}
            />
          )}
        </Card>

        <Card
          title="Package snapshot"
          meta={
            t?.entitlement && current
              ? `${current.name} · snapshot v${t.entitlement.packageVersion} · package now v${current.version}`
              : t?.entitlement
                ? `${t.entitlement.packageId} · snapshot v${t.entitlement.packageVersion}`
                : t
                  ? 'No package yet'
                  : undefined
          }
          action={
            t?.entitlement && canReapply && behind && current ? (
              <Button variant="contained" size="small" onClick={() => setConfirmApply(true)}>
                Apply v{current.version}
              </Button>
            ) : t?.entitlement && current && !behind ? (
              <Badge tone="success" label="Up to date" />
            ) : undefined
          }
        >
          {!t ? (
            <KeyValueSkeleton rows={5} />
          ) : (
            <Stack spacing={2}>
              {behind && (
                <Alert severity="info">
                  The catalog has moved on; this tenant keeps v{t.entitlement?.packageVersion} until
                  someone applies the new version.
                </Alert>
              )}
              {t.entitlement && (
                <KeyValueList
                  items={[
                    { key: 'Features', value: featureList(t.entitlement.features) },
                    {
                      key: 'Price',
                      value: (
                        <Box component="span" sx={MONO}>
                          {t.entitlement.stripePriceId}
                        </Box>
                      ),
                    },
                    { key: 'Snapshot taken', value: formatDate(t.entitlement.snapshotAt) },
                  ]}
                />
              )}
              {canAdopt && adoptable.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                  <Select
                    label="Move to another package"
                    value={adoptId}
                    onChange={setAdoptId}
                    placeholder="Choose a package"
                    options={adoptable.map((p) => ({
                      value: p.packageId,
                      label: `${p.name} · v${p.version}`,
                    }))}
                  />
                  <Button
                    onClick={doAdopt}
                    disabled={!adoptId}
                    busy={adopt.isPending}
                    busyLabel="Moving…"
                    sx={{ mt: 0.5, flexShrink: 0 }}
                  >
                    Move
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </Card>

        <Card title="Subscription">
          {subscription.isLoading ? (
            <KeyValueSkeleton rows={4} />
          ) : subscription.data ? (
            <KeyValueList
              items={[
                { key: 'Status', value: <BillingStatusBadge status={subscription.data.status} /> },
                {
                  key: 'Seats',
                  value: (
                    <Box component="span" sx={TABULAR}>
                      {subscription.data.seats.used} of {subscription.data.seats.licensed}
                    </Box>
                  ),
                },
                {
                  key: 'Model',
                  value: subscription.data.billingModel === 'perSeat' ? 'Per seat' : 'Flat',
                },
                {
                  key: 'Period ends',
                  value: subscription.data.currentPeriodEnd
                    ? `${formatDate(subscription.data.currentPeriodEnd)}${subscription.data.cancelAtPeriodEnd ? ' (cancels then)' : ''}`
                    : '—',
                },
              ]}
            />
          ) : (
            <Alert severity="info">
              {subscription.isError
                ? describeError(subscription.error, 'Billing has no record for this tenant yet.')
                : 'Billing has no record for this tenant yet.'}
            </Alert>
          )}
        </Card>
        <AssistantCard tenantId={id} allowance={t?.entitlement?.assistant} loading={!t} />
      </Box>

      <ConfirmDialog
        open={confirmApply}
        title={current ? `Apply ${current.name} v${current.version}?` : 'Apply?'}
        message={
          t?.entitlement && current
            ? `${t.name} moves from v${t.entitlement.packageVersion} to v${current.version}: ${featureList(current.features)}.`
            : ''
        }
        confirmLabel="Apply"
        busy={reapply.isPending}
        onClose={() => setConfirmApply(false)}
        onConfirm={doApply}
      />
      <ConfirmDialog
        open={confirmSuspend}
        title={t ? `Suspend ${t.name}?` : 'Suspend?'}
        message="Every user of this tenant loses access until it is reinstated. Data is kept."
        confirmLabel="Suspend"
        destructive
        busy={suspend.isPending}
        onClose={() => setConfirmSuspend(false)}
        onConfirm={doSuspend}
      />
    </Page>
  );
}

/**
 * The tenant's assistant spend at first glance (design standard DS-COST): this month and today
 * against its snapshot's limits, with calls and tokens. The limits change with the package.
 */
function AssistantCard({
  tenantId,
  allowance,
  loading,
}: {
  tenantId: string;
  allowance: platform.AssistantAllowance | undefined;
  loading: boolean;
}) {
  const periods = utcPeriods();
  const monthQuery = useMeteringUsage(periods.month, tenantId);
  const todayQuery = useMeteringUsage(periods.day, tenantId);
  const month = assistantTotals(monthQuery.data?.[0]);
  const today = assistantTotals(todayQuery.data?.[0]);
  return (
    <Card
      title="Assistant"
      meta={`${periods.month} and ${periods.day}, UTC`}
      action={<AssistantStateBadge allowance={allowance} month={month} today={today} />}
    >
      {loading || monthQuery.isLoading || todayQuery.isLoading ? (
        <KeyValueSkeleton rows={4} />
      ) : monthQuery.isError || todayQuery.isError ? (
        <Alert severity="error">
          {describeError(
            monthQuery.error ?? todayQuery.error,
            'Could not load the usage. Reload the page to try again.',
          )}
        </Alert>
      ) : (
        <Stack spacing={2}>
          {!allowance && (
            <Alert severity="info">
              The assistant is not in this tenant&apos;s package snapshot, so its calls are refused.
            </Alert>
          )}
          <SpendBar
            label="This month"
            period="this month's"
            resetsEvery="month"
            spentMicroUsd={month.spentMicroUsd}
            limitMicroUsd={allowance?.monthlyLimitMicroUsd}
            calls={month.calls}
          />
          <SpendBar
            label="Today"
            period="today's"
            resetsEvery="day"
            spentMicroUsd={today.spentMicroUsd}
            limitMicroUsd={allowance?.dailyLimitMicroUsd}
            calls={today.calls}
          />
          <KeyValueList
            items={[
              {
                key: 'Tokens this month',
                value: (
                  <Box component="span" sx={TABULAR}>
                    {formatNumber(month.inputTokens)} in · {formatNumber(month.outputTokens)} out
                  </Box>
                ),
              },
              ...(allowance
                ? [
                    {
                      key: 'Models',
                      value: allowance.models.map((m) => MODEL_LABEL[m]).join(', '),
                    },
                    {
                      key: 'Per person',
                      value:
                        allowance.memberMonthlyLimitMicroUsd !== undefined
                          ? `${dollars(allowance.memberMonthlyLimitMicroUsd)} a month`
                          : 'No limit',
                    },
                  ]
                : []),
            ]}
          />
        </Stack>
      )}
    </Card>
  );
}
